import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';
import { useLatest } from 'react-use';

import { formatToLocale } from '@eluve/date-utils';
import { EluveMutationObserver } from '@eluve/eluve-mutation-observer';

/**
 * Hook to detect whether the Eluve extension exists on the page.
 *
 * The extension injects a div with ID 'eluveExtExists' into the page to
 * indicate that it is present.
 *
 * We use a MutationObserver to detect when the div is added to the page
 * since the div is injected asynchronously and it may not be present
 * when the component first renders.
 *
 * @returns {boolean} Whether the Eluve extension exists on the page
 */

type EluveExtData = {
  lastChecked?: string;
  extensionVersion?: string;
  exists?: boolean;
};

export const useEluveExtExists = () => {
  const [eluveExtExists, setEluveExtExists] = useState<boolean | undefined>(
    undefined,
  );
  const [eluveExtData, setEluveExtData] = useLocalStorage<EluveExtData>(
    'ELUVE_EXTENSION',
    {},
  );
  const extExistsRef = useLatest(eluveExtExists);
  // `setEluveExtData` from @uidotdev/usehooks gets a new identity on every
  // localStorage write (its useCallback deps include the read-back JSON). Hold
  // it in a ref so the effects below don't loop on the setter's identity.
  const setEluveExtDataRef = useLatest(setEluveExtData);

  useEffect(() => {
    const checkEluveExt = () => {
      const eluveExt = document.getElementById('eluveExtExists');
      if (eluveExt) {
        setEluveExtExists(true);
        setEluveExtDataRef.current({
          lastChecked: formatToLocale(new Date().toISOString()),
          extensionVersion: eluveExt.innerText,
          exists: true,
        });
        return true;
      }
      return false;
    };

    if (!checkEluveExt()) {
      const observer = new EluveMutationObserver(async () => {
        if (checkEluveExt()) {
          observer.disconnect();
        }
      }, 100);

      observer.start();

      return () => observer.disconnect();
    }
  }, [setEluveExtDataRef]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (extExistsRef.current === undefined) {
        setEluveExtExists(false);
        setEluveExtDataRef.current({
          lastChecked: formatToLocale(new Date().toISOString()),
          extensionVersion: undefined,
          exists: false,
        });
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [extExistsRef, setEluveExtDataRef]);

  return {
    eluveExtExists: eluveExtData?.exists ?? eluveExtExists,
  };
};
